import type { ClinicianLabOrder } from "@care-by-ella/contracts";
import { analyteName, formatRange, formatValue } from "./lab-labels";

// TES-1337 — the doctor's read-only view of what the lab delivered on one
// order: the lab's own comments on the order and on each value, the values,
// and which panel values have not arrived yet (one order's values may come in
// several LabPortalen deliveries). Pure, so it is tested with node --test.
//
// The lab's comments are shown verbatim and are never the doctor's own
// `comment` to the patient.
//
// Swedish strings are PROPOSED copy, except LAB_ORDER_COMMENT_LABEL, whose
// wording was given in the ticket. All of them await a TES-475 row.

export const LAB_ORDER_COMMENT_LABEL = "Kommentar från laboratoriet";
export const LAB_VALUE_COMMENT_LABEL = "Laboratoriets kommentar";
export const LAB_MISSING_VALUES_LABEL = "Värden som inte har kommit än";
export const LAB_PARTIAL_DELIVERY_NOTE =
  "Delsvar: laboratoriet har inte skickat alla värden än.";
// TES-1337 — InfoSolutions' own word for Reply@StatusCode P/PPA.
export const LAB_PRELIMINARY_LABEL = "Preliminärsvar";
export const LAB_REFERENCE_LABEL = "Labbets referens";
// TES-1341 — proposed copy, awaiting a TES-475 row, except
// LAB_ATTACHMENT_IN_LABPORTALEN_LABEL (contracts), whose wording is the
// ticket's.
export const LAB_CORRECTED_LABEL = "Rättat av labbet";
export const LAB_EARLIER_VALUE_LABEL = "Tidigare värde";

export interface LabOrderResultsView {
  // One line per lab comment on the order, oldest delivery first. `when` is
  // the delivery's report time, shown only when there are several
  // deliveries to tell apart.
  orderComments: Array<{ key: string; text: string; when: string | null }>;
  rows: Array<{
    analyteKey: string;
    name: string;
    value: string;
    range: string | null;
    bucket: ClinicianLabOrder["results"][number]["bucket"];
    labComment: string | null;
    // TES-1337 — the value came in a preliminary reply and may change.
    preliminary: boolean;
    // The lab's own interval as it shows it, with its out-of-range mark.
    labReference: string | null;
    // TES-1341 — a lab correction replaced this value.
    corrected: boolean;
    // TES-1341 — the values this one replaced, newest first, as
    // "<value> <unit>" with the date it was replaced.
    earlier: Array<{ value: string; when: string }>;
  }>;
  // Display names of the panel values still missing; empty when none.
  missing: string[];
  // TES-1341 — the lab attached files (they stay in LabPortalen): their
  // names, as the lab gave them; empty when none.
  attachments: string[];
  // Whether there is anything to render at all.
  isEmpty: boolean;
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

export function labOrderResultsView(
  order: Pick<
    ClinicianLabOrder,
    "results" | "labComments" | "missingAnalytes"
  > &
    Partial<Pick<ClinicianLabOrder, "labAttachments">>,
): LabOrderResultsView {
  const comments = order.labComments ?? [];
  const deliveries = new Set(comments.map((c) => c.interchangeRef));
  const orderComments = comments.map((c, i) => ({
    key: `${c.interchangeRef}:${i}`,
    text: c.text,
    when: deliveries.size > 1 && c.reportTime ? fmt(c.reportTime) : null,
  }));
  const rows = order.results.map((r) => ({
    analyteKey: r.analyteKey,
    name: analyteName(r.analyteKey),
    value: `${formatValue(r.value, r.valueText)} ${r.unit}`.trim(),
    range: formatRange(r.refLow, r.refHigh),
    bucket: r.bucket,
    labComment: r.labComment ?? null,
    preliminary: r.labPreliminary ?? false,
    labReference:
      r.labReference || r.labRefMark
        ? [r.labReference, r.labRefMark].filter(Boolean).join(" ")
        : null,
    corrected: !!r.labCorrectedAt,
    earlier: [...(r.labValueHistory ?? [])].reverse().map((h) => ({
      value: `${formatValue(h.value, h.valueText)} ${h.unit}`.trim(),
      when: fmt(h.replacedAt),
    })),
  }));
  const missing = (order.missingAnalytes ?? []).map(analyteName);
  const attachments = (order.labAttachments ?? []).map(
    (a) => a.name ?? a.type ?? a.mimeType ?? "",
  );
  return {
    orderComments,
    rows,
    missing,
    attachments,
    isEmpty:
      rows.length === 0 &&
      orderComments.length === 0 &&
      attachments.length === 0,
  };
}
